import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Warehouse, Users, Sliders } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LocationsSettings } from "../components/settings/LocationsSettings";
import { UsersSettings } from "../components/settings/UsersSettings";
import { AppSettings } from "../components/settings/AppSettings";
import { NotificationPreferences } from "../components/settings/NotificationPreferences";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [storageAreas, setStorageAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoized pending users
  const pendingUsers = useMemo(() =>
    users.filter(u => u.status === "pending" || (!u.status && u.role !== "admin")),
    [users]
  );

  // Location dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: "", address: "" });

  // Storage area dialog
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [newArea, setNewArea] = useState({ name: "", order: 0 });

  // Edit dialogs
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editAreaOpen, setEditAreaOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);

  // Permissions dialog
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permForm, setPermForm] = useState({
    inventory: { read: true, write: true, edit: true },
    waste_log: { read: true, write: true, edit: true },
    transfers: { read: true, write: true, edit: true },
    allowed_locations: []
  });
  const [savingPerms, setSavingPerms] = useState(false);

  // App settings
  const [appSettings, setAppSettings] = useState({
    require_transfer_approval: true,
    waste_reminder_enabled: true,
    waste_reminder_time: "18:00"
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // User notification preferences
  const [notifPrefs, setNotifPrefs] = useState({
    transfer_created: true,
    transfer_approved: true,
    transfer_rejected: true,
    transfer_received: true,
    waste_log_daily_reminder: true,
    waste_log_weekly_summary: true,
    email_enabled: true,
    in_app_enabled: true,
    daily_reminder_time: "18:00"
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAppSettings();
    fetchNotificationPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchStorageAreas(selectedLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const fetchData = async () => {
    try {
      const [locRes, userRes] = await Promise.all([
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setLocations(locRes.data);
      setUsers(userRes.data);
      if (locRes.data.length > 0) {
        setSelectedLocation(locRes.data[0].location_id);
      }
    } catch (error) {
      toast.error("Failed to load settings data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageAreas = async (locationId) => {
    try {
      const response = await axios.get(`${API}/storage-areas?location_id=${locationId}`, { withCredentials: true });
      setStorageAreas(response.data);
    } catch (error) {
      toast.error("Failed to load storage areas");
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.name) {
      toast.error("Please enter a location name");
      return;
    }

    try {
      await axios.post(`${API}/locations`, newLocation, { withCredentials: true });
      toast.success("Location added");
      setLocationDialogOpen(false);
      setNewLocation({ name: "", address: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add location");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm("Delete this location? This will also delete all storage areas and products.")) return;

    try {
      await axios.delete(`${API}/locations/${locationId}`, { withCredentials: true });
      toast.success("Location deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  const handleAddArea = async () => {
    if (!newArea.name || !selectedLocation) {
      toast.error("Please enter an area name");
      return;
    }

    try {
      await axios.post(`${API}/storage-areas`, {
        ...newArea,
        location_id: selectedLocation
      }, { withCredentials: true });
      toast.success("Storage area added");
      setAreaDialogOpen(false);
      setNewArea({ name: "", order: storageAreas.length });
      fetchStorageAreas(selectedLocation);
    } catch (error) {
      toast.error("Failed to add storage area");
    }
  };

  const handleDeleteArea = async (areaId) => {
    if (!window.confirm("Delete this storage area? This will also delete all products in it.")) return;

    try {
      await axios.delete(`${API}/storage-areas/${areaId}`, { withCredentials: true });
      toast.success("Storage area deleted");
      fetchStorageAreas(selectedLocation);
    } catch (error) {
      toast.error("Failed to delete storage area");
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation({ ...location });
    setEditLocationOpen(true);
  };

  const handleSaveLocation = async () => {
    if (!editingLocation?.name) {
      toast.error("Please enter a location name");
      return;
    }

    try {
      await axios.put(`${API}/locations/${editingLocation.location_id}`, {
        name: editingLocation.name,
        address: editingLocation.address || ""
      }, { withCredentials: true });
      toast.success("Location updated");
      setEditLocationOpen(false);
      setEditingLocation(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const handleEditArea = (area) => {
    setEditingArea({ ...area });
    setEditAreaOpen(true);
  };

  const handleSaveArea = async () => {
    if (!editingArea?.name) {
      toast.error("Please enter an area name");
      return;
    }

    try {
      await axios.put(`${API}/storage-areas/${editingArea.area_id}`, {
        name: editingArea.name
      }, { withCredentials: true });
      toast.success("Storage area updated");
      setEditAreaOpen(false);
      setEditingArea(null);
      fetchStorageAreas(selectedLocation);
    } catch (error) {
      toast.error("Failed to update storage area");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleChangeStatus = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/users/${userId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`User ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Remove this user? They will need to be re-approved to access the app.")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("User removed");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };

  const handleOpenPermissions = async (usr) => {
    setPermUser(usr);
    try {
      const res = await axios.get(`${API}/users/${usr.user_id}/permissions`, { withCredentials: true });
      setPermForm({
        inventory: res.data.inventory || { read: true, write: true, edit: true },
        waste_log: res.data.waste_log || { read: true, write: true, edit: true },
        transfers: res.data.transfers || { read: true, write: true, edit: true },
        allowed_locations: res.data.allowed_locations || []
      });
    } catch {
      setPermForm({
        inventory: { read: true, write: true, edit: true },
        waste_log: { read: true, write: true, edit: true },
        transfers: { read: true, write: true, edit: true },
        allowed_locations: []
      });
    }
    setPermDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    setSavingPerms(true);
    try {
      await axios.put(`${API}/users/${permUser.user_id}/permissions`, permForm, { withCredentials: true });
      toast.success(`Permissions updated for ${permUser.name}`);
      setPermDialogOpen(false);
      setPermUser(null);
    } catch (error) {
      toast.error("Failed to update permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const togglePermission = (module, action) => {
    setPermForm(prev => ({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module][action] }
    }));
  };

  const toggleLocationRestriction = (locationId) => {
    setPermForm(prev => {
      const current = prev.allowed_locations || [];
      const updated = current.includes(locationId)
        ? current.filter(id => id !== locationId)
        : [...current, locationId];
      return { ...prev, allowed_locations: updated };
    });
  };

  const fetchAppSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/app`, { withCredentials: true });
      setAppSettings({
        require_transfer_approval: res.data.require_transfer_approval ?? true,
        waste_reminder_enabled: res.data.waste_reminder_enabled ?? true,
        waste_reminder_time: res.data.waste_reminder_time ?? "18:00"
      });
    } catch {
      console.error("Failed to load app settings");
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`, { withCredentials: true });
      setNotifPrefs(res.data);
    } catch {
      console.error("Failed to load notification preferences");
    }
  };

  const handleToggleTransferApproval = async (checked) => {
    setSavingSettings(true);
    try {
      const updated = { ...appSettings, require_transfer_approval: checked };
      await axios.put(`${API}/settings/app`, updated, { withCredentials: true });
      setAppSettings(updated);
      toast.success(checked ? "Transfer approvals required" : "Transfers auto-approved");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleWasteReminder = async (checked) => {
    setSavingSettings(true);
    try {
      const updated = { ...appSettings, waste_reminder_enabled: checked };
      await axios.put(`${API}/settings/app`, updated, { withCredentials: true });
      setAppSettings(updated);
      toast.success(checked ? "Daily waste reminders enabled" : "Daily waste reminders disabled");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleReminderTimeChange = async (time) => {
    setSavingSettings(true);
    try {
      const updated = { ...appSettings, waste_reminder_time: time };
      await axios.put(`${API}/settings/app`, updated, { withCredentials: true });
      setAppSettings(updated);
      toast.success(`Reminder time set to ${time}`);
    } catch (error) {
      toast.error("Failed to update reminder time");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleNotifPrefChange = async (key, value) => {
    setSavingNotifPrefs(true);
    try {
      const updated = { ...notifPrefs, [key]: value };
      await axios.put(`${API}/notifications/preferences`, { [key]: value }, { withCredentials: true });
      setNotifPrefs(updated);
      toast.success("Notification preference updated");
    } catch (error) {
      toast.error("Failed to update preference");
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const handleSendTestReminder = async () => {
    try {
      await axios.post(`${API}/notifications/send-waste-reminder`, {}, { withCredentials: true });
      toast.success("Test reminder sent to all location users");
    } catch (error) {
      toast.error("Failed to send test reminder");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
          SETTINGS
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage locations, storage areas, and users
        </p>
      </div>

      <Tabs defaultValue="locations" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="locations" className="font-heading tracking-wide">
            <Warehouse size={16} className="mr-2" />
            LOCATIONS
          </TabsTrigger>
          <TabsTrigger value="users" className="font-heading tracking-wide">
            <Users size={16} className="mr-2" />
            USERS
          </TabsTrigger>
          <TabsTrigger value="app-settings" className="font-heading tracking-wide" data-testid="app-settings-tab">
            <Sliders size={16} className="mr-2" />
            APP SETTINGS
          </TabsTrigger>
        </TabsList>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-6 space-y-6">
          <LocationsSettings
            locations={locations} storageAreas={storageAreas}
            selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}
            locationDialogOpen={locationDialogOpen} setLocationDialogOpen={setLocationDialogOpen}
            newLocation={newLocation} setNewLocation={setNewLocation}
            onAddLocation={handleAddLocation} onDeleteLocation={handleDeleteLocation}
            areaDialogOpen={areaDialogOpen} setAreaDialogOpen={setAreaDialogOpen}
            newArea={newArea} setNewArea={setNewArea}
            onAddArea={handleAddArea} onDeleteArea={handleDeleteArea}
            editLocationOpen={editLocationOpen} setEditLocationOpen={setEditLocationOpen}
            editingLocation={editingLocation} setEditingLocation={setEditingLocation}
            onEditLocation={handleEditLocation} onSaveLocation={handleSaveLocation}
            editAreaOpen={editAreaOpen} setEditAreaOpen={setEditAreaOpen}
            editingArea={editingArea} setEditingArea={setEditingArea}
            onEditArea={handleEditArea} onSaveArea={handleSaveArea}
          />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6 space-y-6">
          <UsersSettings
            users={users} pendingUsers={pendingUsers} locations={locations}
            onChangeRole={handleChangeRole} onChangeStatus={handleChangeStatus} onDeleteUser={handleDeleteUser}
            permDialogOpen={permDialogOpen} setPermDialogOpen={setPermDialogOpen}
            permUser={permUser} permForm={permForm} savingPerms={savingPerms}
            onOpenPermissions={handleOpenPermissions} onSavePermissions={handleSavePermissions}
            onTogglePermission={togglePermission} onToggleLocationRestriction={toggleLocationRestriction}
          />
        </TabsContent>

        {/* App Settings Tab */}
        <TabsContent value="app-settings" className="mt-6 space-y-6">
          <AppSettings
            isAdmin={isAdmin}
            appSettings={appSettings} savingSettings={savingSettings}
            onToggleTransferApproval={handleToggleTransferApproval}
            onToggleWasteReminder={handleToggleWasteReminder}
            onReminderTimeChange={handleReminderTimeChange}
            onSendTestReminder={handleSendTestReminder}
          />
          <NotificationPreferences
            notifPrefs={notifPrefs} savingNotifPrefs={savingNotifPrefs}
            onChange={handleNotifPrefChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
